import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useCallback, useEffect, useState } from "react"
import { useCrudContext } from "@/context/crudContext"
import ClearIcon from "@mui/icons-material/Clear"
import { Textarea } from "@mantine/core"
import { DevTool } from "@/components/FormDevTool"
import MultiSelect from "@/components/Select/MultipleSelect"
import Grid from "@mui/material/Grid2"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import useSuppliers from "@/hooks/useSuppliers"
import useTags from "@/hooks/useTags"
import useAttributes from "@/hooks/useAttributes"
import useBrands from "@/hooks/useBrands"
import AddAttributeModal from "../attributes/Model"
import { apiFetch } from "@/hooks/apiFetch"
import ImageIcon from "@mui/icons-material/Image"
import StarIcon from "@mui/icons-material/Star"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import DeleteIcon from "@mui/icons-material/Delete"

const ProductSchema = z.object({
  name: z.string().min(2),
  sku: z.string().min(1),
  sell_price: z.preprocess(
    (val) =>
      val === "" || val === null || Number.isNaN(Number(val))
        ? undefined
        : Number(val),
    z.number({ required_error: "Sell price is required" }).min(0)
  ),
  attribute_ids: z.array(z.number()).default([]),
})

const AttributeSchema = z.object({
  id: z.number().positive(),
  key: z.string(),
  attribute: z.string(),
  value: z.string().optional().nullable(),
})
const TagSchema = z.object({
  id: z.number().positive(),
  name: z.string(),
})

const ProductVariantSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters long"), // Ensures the name is not empty and has a min length of 3.
  code: z.string().min(2, "Code must be at least 2 characters long"), // Ensures the name is not empty and has a min length of 3.
  sell_price: z.preprocess(
    (val) =>
      val === "" ||
      val === null ||
      val === undefined ||
      Number.isNaN(Number(val))
        ? null
        : Number(val),
    z.number().min(0).nullable()
  ),
  brand_id: z.number().nullable().optional(),

  products: z
    .array(ProductSchema)
    .min(1, "At least one product must be included"),

  product_attributes: z.array(AttributeSchema).default([]),
  tags: z.array(TagSchema).default([]),
  status: z.preprocess((val) => Boolean(val), z.boolean()),
  description: z.string().nullish(),
})

const AddProductVariantModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()
  const [openAttributeModel, setOpenAttributeModel] = useState(false)

  const {
    api,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    watch,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    resolver: zodResolver(ProductVariantSchema),
  })
  const { attributes, isLoading: attributeLoading } = useAttributes()
  const { tags, isLoading: tagLoading } = useTags()
  const { brands, isLoading: brandLoading } = useBrands()
  const [images, setImages] = useState([])
  const [pendingImages, setPendingImages] = useState([]) // For new variant creation
  const [uploading, setUploading] = useState(false)

  const defaultOnClose = () => {
    setOpenDialog(false)
    setImages([])
    setPendingImages([])
  }

  useEffect(() => {
    // ProductVariantTable already loads the full variant for edit. Reusing its
    // response avoids issuing the same detail request again for images.
    setImages(selectedRow?.images || [])
    setPendingImages([])
  }, [selectedRow])

  const handleAttributeModelClose = useCallback(() => {
    setOpenAttributeModel(false)
    queryClient.invalidateQueries("attribute-all")
  }, [queryClient])

  const setFormValues = (data) => {
    if (data) {
      // API products carry product_attributes objects, not the attribute_ids
      // array the schema and afterUpdate() payload expect — derive it
      const normalized = {
        ...data,
        products: (data.products ?? []).map((product) => ({
          ...product,
          attribute_ids:
            product.attribute_ids ??
            product.product_attributes?.map((attr) => attr.id) ??
            [],
        })),
      }
      Object.entries(normalized).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset({
        name: "",
        cost_price: "",
        sell_price: "",
        description: "",
        status: true,
      })
    }
  }

  const onInvalid = (formErrors) => {
    const firstError = (obj, path = []) => {
      if (!obj || typeof obj !== "object") return null
      if (obj.message) return { path, message: obj.message }
      for (const [key, value] of Object.entries(obj)) {
        const found = firstError(value, [...path, key])
        if (found) return found
      }
      return null
    }
    const error = firstError(formErrors)
    toast.error(
      error
        ? `${error.path.join(".")}: ${error.message}`
        : "Form validation failed"
    )
  }

  const onSubmit = async (data) => {
    if (data.cost_price) {
      data.cost_price = Number(data.cost_price)
    }
    // backend validates status against int-backed enum (1|0)
    data.status = data.status ? 1 : 0

    const method = selectedRow ? "PUT" : "POST" // Use PUT for updating, POST for adding
    const url = selectedRow
      ? `product-variant/${selectedRow.id}`
      : "product-variant"

    const response = await api(url, {
      method,
      body: JSON.stringify(data),
    })

    if (response) {
      const variantId = response?.data?.id || selectedRow?.id

      // If there are pending images and variant was just created, upload them
      if (!selectedRow && pendingImages.length > 0 && variantId) {
        try {
          await handleUploadPendingImages(variantId)
        } catch (error) {
          toast.error("Product variant created but failed to upload images")
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["product-variant"] })

      // A duplicate article is attached to its existing variant by the API.
      // Its id is intentionally not returned, so clear inactive detail caches
      // and make the next Edit request load that variant's new products.
      queryClient.removeQueries({
        queryKey: ["product-variant-detail"],
        type: "inactive",
      })
      toast.success(
        `Product Variant successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  const handleUploadPendingImages = async (variantId) => {
    if (pendingImages.length === 0) return

    const formData = new FormData()
    pendingImages.forEach((image) => {
      formData.append("images[]", image.file)
    })

    const response = await apiFetch(`image/group/${variantId}`, {
      method: "POST",
      body: formData,
    })

    if (response?.data) {
      toast.success("Images uploaded successfully")
    }
  }

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    // If editing existing variant, upload immediately
    if (selectedRow?.id) {
      setUploading(true)
      try {
        const formData = new FormData()
        files.forEach((file) => {
          formData.append("images[]", file)
        })

        const response = await apiFetch(`image/group/${selectedRow.id}`, {
          method: "POST",
          body: formData,
        })

        if (response?.data) {
          setImages([...images, ...response.data])
          queryClient.invalidateQueries({
            queryKey: ["product-variant"],
          })
          toast.success("Images uploaded successfully")
        }
      } catch (error) {
        toast.error(error?.message || "Failed to upload images")
      } finally {
        setUploading(false)
        e.target.value = ""
      }
    } else {
      // If creating new variant, store files temporarily
      const newFiles = files.map((file, index) => ({
        id: Date.now() + Math.random(),
        file,
        url: URL.createObjectURL(file),
        original_name: file.name,
        is_thumbnail: pendingImages.length === 0 && index === 0, // First image is thumbnail
      }))
      setPendingImages([...pendingImages, ...newFiles])
      e.target.value = ""
    }
  }

  const handleSetThumbnail = async (imageId) => {
    // If it's a pending image (new variant), just update state
    if (pendingImages.some((img) => img.id === imageId)) {
      setPendingImages(
        pendingImages.map((img) => ({
          ...img,
          is_thumbnail: img.id === imageId,
        }))
      )
      return
    }

    // If it's an existing image, update on server
    try {
      await apiFetch(`image/${imageId}/set-thumbnail`, {
        method: "POST",
      })
      setImages(
        images.map((img) => ({
          ...img,
          is_thumbnail: img.id === imageId,
        }))
      )
      queryClient.invalidateQueries({
        queryKey: ["product-variant", selectedRow?.id],
      })
      toast.success("Thumbnail updated successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to set thumbnail")
    }
  }

  const handleDeleteImage = async (imageId) => {
    // If it's a pending image (new variant), just remove from state
    const pendingImage = pendingImages.find((img) => img.id === imageId)
    if (pendingImage) {
      URL.revokeObjectURL(pendingImage.url)
      setPendingImages(pendingImages.filter((img) => img.id !== imageId))
      return
    }

    // If it's an existing image, delete on server
    try {
      await apiFetch(`image/${imageId}`, {
        method: "DELETE",
      })
      setImages(images.filter((img) => img.id !== imageId))
      queryClient.invalidateQueries({ queryKey: ["product-variant"] })
      toast.success("Image deleted successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to delete image")
    }
  }

  // generate product
  const products = watch("products", [])

  const generateProductCombinations = () => {
    const attributes = getValues("product_attributes")
    const productName = getValues("name")
    const productCode = getValues("code")
    const costPrice = getValues("cost_price")
    const sellPrice = getValues("sell_price")

    if (attributes === undefined || attributes.length == 0) {
      return [{}] // If no attributes, return an empty product
    }

    // Group attributes by their keys
    const groupedAttributes = attributes.reduce((acc, attr) => {
      const { key, attribute } = attr
      if (!acc[key]) acc[key] = []
      acc[key].push(attr)
      return acc
    }, {})
    const keys = Object.keys(groupedAttributes) // List of keys for attributes
    const combinations = []

    // Recursive function to generate combinations
    const createCombinations = (
      prefix = { attributeIds: [], name: "", sku: "" },
      index = 0
    ) => {
      if (index === keys.length) {
        combinations.push({
          attributeIds: [...prefix.attributeIds], // Add the attribute IDs
          name: prefix.name.trim(),
          sku: prefix.sku, // Create combination name
        })
        return
      }
      const currentKey = keys[index] // Get the current key

      // Iterate over each attribute in the current key group
      groupedAttributes[currentKey].forEach(({ id, attribute }) => {
        createCombinations(
          {
            attributeIds: [...prefix.attributeIds, id], // Append attribute ID
            name: `${prefix.name}${currentKey}-${attribute} `,
            sku: `${prefix.sku}-${attribute}`, // Append attribute name
          },
          index + 1
        )
      })
    }

    createCombinations() // Start creating combinations

    // Map the generated combinations to create product data.
    // Products already saved in DB (they carry a backend `code`) keep their own
    // values — regen must not clobber their prices; fresh combos still follow
    // the variant-level price/name so create keeps propagating.
    const currentProducts = getValues("products") || []
    const generatedProduct = combinations.map((combination, index) => {
      const sku = `${productCode}${combination.sku}`
      const existing = currentProducts.find(
        (product) => product.sku === sku && product.code
      )
      if (existing) {
        return {
          ...existing,
          attribute_ids: existing.attribute_ids?.length
            ? existing.attribute_ids
            : combination.attributeIds,
        }
      }
      return {
        id: index, // Unique ID for the product
        name: `${productName} ${combination.name}`, // Product name + combination name
        sell_price: sellPrice, // Sell price from form
        cost_price: costPrice, // Cost price from form
        sku, // Product code + combination
        stock: 0, // Initial stock (default 0)
        attribute_ids: combination.attributeIds, // Array of attribute IDs for this combination
      }
    })
    // Set the generated products in the form state
    setValue("products", generatedProduct)

    return generatedProduct
  }

  const handleProductProperties = (index, newValue, property) => {
    const currentProducts = getValues("products")
    const updatedProducts = currentProducts.map((product, i) =>
      i === index ? { ...product, [property]: newValue } : product
    )
    setValue("products", updatedProducts)
  }

  const handleRemoveProduct = (itemToRemove) => {
    const currentProducts = getValues("products")
    const updatedProducts = currentProducts.filter(
      (item) => item.id !== itemToRemove.id
    )

    console.log(currentProducts, updatedProducts)
    setValue("products", updatedProducts)
  }

  useEffect(() => {
    setFormValues(selectedRow)
  }, [selectedRow, setValue, reset])

  const product_attributes = watch("product_attributes")
  const productName = watch("name")
  const cp = watch("cost_price")
  const sp = watch("sell_price")
  const code = watch("code")

  useEffect(() => {
    generateProductCombinations()
  }, [product_attributes, productName, cp, sp, code])

  const handleReset = () => {
    setFormValues(selectedRow)
    setValue("products", [])
  }

  return (
    <>
      <Modal
        open={open ? open : openDialog}
        onClose={defaultOnClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            margin: "100px auto",
            bgcolor: "white",
            maxHeight: "90vh",
            width: "80vw",
            overflowY: "auto",
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: 1,
              background: "#f6f7f9",
              padding: { xs: "15px 20px", md: "25px" },
            }}
            className="rmu-modal-header"
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "16px", md: "18px" },
              }}
              className="text-black"
            >
              {selectedRow
                ? "Update Product Variant"
                : "Add New Product Variant"}
            </Typography>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          <Box
            sx={{ padding: 2 }}
            component="form"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "500",
                fontSize: { xs: "12px", md: "16px" },
                marginBottom: 2,
              }}
              className="text-black"
            >
              Basic Information
            </Typography>
            <div className="flex gap-4 items-center mb-4">
              <TextField
                fullWidth
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register("name")}
              />

              <Controller
                name="status"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Status"
                  />
                )}
              />
            </div>
            {/* basic information */}
            <Grid xs={12} md={12} lg={12} container spacing={2}>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <TextField
                  fullWidth
                  label="Selling Price"
                  type="number"
                  sx={{ marginBottom: 2 }}
                  error={!!errors.sell_price}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                  helperText={errors.sell_price?.message}
                  {...register("sell_price", {
                    valueAsNumber: true,
                  })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <TextField
                  fullWidth
                  label="Article Name"
                  sx={{ marginBottom: 2 }}
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  {...register("code")}
                />
              </Grid>
              <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                <MultiSelect
                  name="tags"
                  label="Tags"
                  options={tags}
                  control={control}
                  loading={tagLoading}
                  defaultValues={selectedRow?.tags}
                  controller={Controller}
                  optionLabel="name"
                  error={!!errors.tags}
                  helperText={errors.tags?.message}
                />
              </Grid>
              <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                <SingleSelect
                  name="brand_id"
                  controller={Controller}
                  control={control}
                  loading={brandLoading}
                  label="Brand"
                  selectedValue={selectedRow?.brand_id}
                  options={brands ?? []}
                  error={!!errors.brand_id}
                  helperText={errors.brand_id?.message}
                  optionLabel={"name"}
                  optionValue="id"
                />
              </Grid>
              <Grid size={{ sm: 12, md: 6, lg: 8 }}>
                <div className="flex">
                  <MultiSelect
                    name="product_attributes"
                    label="Attributes"
                    loading={attributeLoading}
                    options={attributes}
                    defaultValues={selectedRow?.product_attributes}
                    control={control}
                    controller={Controller}
                    optionLabel={["key", "attribute", "value"]}
                    error={!!errors.product_attributes}
                    helperText={errors.product_attributes?.message}
                  />

                  <Button
                    variant="contained"
                    onClick={() => setOpenAttributeModel(true)}
                    sx={{
                      minWidth: "48px",
                      width: "48px",
                      height: "55px",
                      padding: 0,
                    }}
                  >
                    <AddIcon />
                  </Button>
                </div>
              </Grid>
            </Grid>

            {/* description field here  */}
            <div className="mt-4">
              <Textarea
                placeholder="description"
                error={!!errors.description}
                helperText={errors.description?.message}
                {...register("description")}
              />
            </div>

            {/* generated product */}
            <Divider sx={{ marginBottom: 2 }} />
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "500",
                fontSize: { xs: "12px", md: "16px" },
                marginBottom: 2,
              }}
              className="text-black"
            >
              Products
            </Typography>
            <Controller
              name="products"
              control={control}
              defaultValue={[]}
              render={({
                field: { onChange: fieldOnChange, value: fieldValue },
                fieldState: { error, isTouched, isDirty },
              }) => (
                <>
                  {fieldValue.map((product, index) => (
                    <Grid
                      key={index}
                      sx={{ marginTop: 1 }}
                      container
                      spacing={2}
                    >
                      <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                        <FormControl
                          fullWidth
                          error={!!errors.products?.[index]?.name}
                        >
                          <TextField
                            label="Product Name"
                            value={product.name}
                            variant="filled"
                            helperText={
                              errors.products?.[index]?.name?.message || ""
                            }
                            id={`productName${index}`}
                            name="productName"
                            onChange={(event) =>
                              handleProductProperties(
                                index,
                                event.target.value,
                                "name"
                              )
                            }
                            sx={{
                              "& .MuiInputBase-root": {
                                border: "1px solid #D5D9E2",
                                backgroundColor: "#fff",
                                borderRadius: "7px",
                              },
                              "& .MuiInputBase-root::before": {
                                border: "none",
                              },
                              "& .MuiInputBase-root:hover::before": {
                                border: "none",
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                        <FormControl
                          fullWidth
                          error={!!errors.products?.[index]?.sku}
                        >
                          <TextField
                            label="Sku"
                            value={product.sku}
                            variant="filled"
                            error={!!errors.products?.[index]?.sku}
                            helperText={
                              errors.products?.[index]?.sku?.message || ""
                            }
                            onChange={(event) =>
                              handleProductProperties(
                                index,
                                event.target.value,
                                "sku"
                              )
                            }
                            sx={{
                              "& .MuiInputBase-root": {
                                border: "1px solid #D5D9E2",
                                backgroundColor: "#fff",
                                borderRadius: "7px",
                              },
                              "& .MuiInputBase-root::before": {
                                border: "none",
                              },
                              "& .MuiInputBase-root:hover::before": {
                                border: "none",
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ sm: 12, md: 6, lg: 3 }}>
                        <FormControl
                          fullWidth
                          error={!!errors.products?.[index]?.sell_price}
                        >
                          <TextField
                            label="Sell Price"
                            value={product.sell_price}
                            variant="filled"
                            type="number"
                            id={`sellPrice${index}`}
                            name="sellPrice"
                            error={!!errors.products?.[index]?.sell_price}
                            helperText={
                              errors.products?.[index]?.sell_price?.message ||
                              ""
                            }
                            onChange={(event) =>
                              handleProductProperties(
                                index,
                                event.target.value,
                                "sell_price"
                              )
                            }
                            sx={{
                              "& .MuiInputBase-root": {
                                border: "1px solid #D5D9E2",
                                backgroundColor: "#fff",
                                borderRadius: "7px",
                              },
                              "& .MuiInputBase-root::before": {
                                border: "none",
                              },
                              "& .MuiInputBase-root:hover::before": {
                                border: "none",
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>

                      {/* <Grid size={{ xs: 4, md: 4, lg: 1 }} className="mt-2">
                        <IconButton
                          aria-label="remove"
                          onClick={() => handleRemoveProduct(product)}
                        >
                          <ClearIcon />
                        </IconButton>
                      </Grid> */}
                    </Grid>
                  ))}
                </>
              )}
            />
            {/* Image Upload Section - Show for both new and existing variants */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Product Images
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="variant-image-upload"
                  multiple
                  type="file"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                <label htmlFor="variant-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<ImageIcon />}
                    disabled={uploading}
                    sx={{ mb: 2 }}
                  >
                    {uploading ? "Uploading..." : "Upload Images"}
                  </Button>
                </label>
              </Box>

              {/* Existing images (editing) + pending images (new variant) */}
              {[...images, ...pendingImages].length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {[...images, ...pendingImages].map((image) => (
                    <Box
                      key={image.id}
                      sx={{
                        position: "relative",
                        border: image.is_thumbnail
                          ? "3px solid #1976d2"
                          : "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        aspectRatio: "1",
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.original_name || "Product image"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSetThumbnail(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title={
                            image.is_thumbnail
                              ? "Thumbnail"
                              : "Set as thumbnail"
                          }
                        >
                          {image.is_thumbnail ? (
                            <StarIcon color="primary" />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteImage(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title="Delete image"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                      {image.is_thumbnail && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(25, 118, 210, 0.9)",
                            color: "white",
                            textAlign: "center",
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          Thumbnail
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
            {/* buttons */}
            <Grid size={12} mt={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                }}
                marginTop={5}
              >
                <Button
                  onClick={handleReset}
                  variant="text"
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "13px",
                    padding: "11px 30px",
                  }}
                >
                  Reset
                </Button>

                <Button
                  onClick={onClose ? onClose : defaultOnClose}
                  variant="outlined"
                  color="error"
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "13px",
                    padding: "11px 30px",
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "13px",
                    padding: "11px 30px",
                    color: "#fff !important",
                  }}
                >
                  {selectedRow ? "Update" : "Create"}
                </Button>
              </Box>
            </Grid>
            <DevTool control={control} />
          </Box>
        </Box>
      </Modal>

      {openAttributeModel ? (
        <AddAttributeModal
          open={openAttributeModel}
          onClose={handleAttributeModelClose}
        />
      ) : null}
    </>
  )
}

export default AddProductVariantModal
